import { useState, useEffect } from 'react'
import { api } from '@/lib/api'
import { PageLoading, EmptyState } from '@/shared/components/EmptyState'
import { FeedbackMessage } from '@/shared/components/FeedbackMessage'
import { useToast } from '@/context/ToastContext'
import s from '@/features/org/org.module.css'

export function NotificationsSettings({ orgId }: { orgId: string; canAdmin: boolean }) {
  const { showToast } = useToast()
  const [preference, setPreference] = useState<'none' | 'daily' | 'weekly' | 'monthly'>('none')
  const [busy, setBusy] = useState(false)
  const [loaded, setLoaded] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)

  useEffect(() => {
    api.get(`/api/orgs/${orgId}/settings`)
      .then((data: unknown) => {
        const pref = (data as { provider_update_notifications?: string }).provider_update_notifications as 'none' | 'daily' | 'weekly' | 'monthly' | undefined
        if (pref && ['none', 'daily', 'weekly', 'monthly'].includes(pref)) {
          setPreference(pref)
        }
        setLoaded(true)
      })
      .catch((err: Error) => {
        setLoadError(err.message)
        setLoaded(true)
      })
  }, [orgId])

  async function handleSave(newPref: 'none' | 'daily' | 'weekly' | 'monthly') {
    setBusy(true)
    try {
      await api.patch(`/api/orgs/${orgId}/notifications`, {
        provider_update_notifications: newPref,
      })
      setPreference(newPref)
      showToast('Notification preferences updated.', 'success')
    } catch (err) {
      showToast((err as Error).message, 'error')
    } finally {
      setBusy(false)
    }
  }

  const options: Array<{ value: 'none' | 'daily' | 'weekly' | 'monthly'; label: string; description: string }> = [
    { value: 'none', label: 'None', description: 'No notifications' },
    { value: 'daily', label: 'Daily', description: 'Daily digest of provider updates' },
    { value: 'weekly', label: 'Weekly', description: 'Weekly digest of provider updates' },
    { value: 'monthly', label: 'Monthly', description: 'Monthly digest of provider updates' },
  ]

  if (!loaded) {
    return <PageLoading />
  }

  if (loadError) {
    return loadError.toLowerCase().includes('forbidden') || loadError.toLowerCase().includes('not authorized') || loadError.toLowerCase().includes('permission')
      ? <EmptyState icon="🔒" title="Access restricted" subtitle="You don't have permission to view notification settings. Ask an org owner or admin to grant you access." />
      : <FeedbackMessage ok={false} msg={loadError} />
  }

  return (
    <div>
      <p style={{ fontFamily: 'var(--body)', fontSize: '0.82rem', color: 'var(--text2)', marginBottom: 16 }}>
        Receive email digests about provider updates that are outside your version constraints for Terraform lock files.
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {options.map((opt) => (
          <label
            key={opt.value}
            className={`${s.notifOption}${preference === opt.value ? ` ${s.notifOptionActive}` : ''}`}
          >
            <input
              type="radio"
              name="notifications"
              value={opt.value}
              checked={preference === opt.value}
              disabled={busy}
              onChange={(e) => handleSave(e.target.value as typeof preference)}
              style={{ cursor: 'pointer', width: 18, height: 18 }}
            />
            <div>
              <div style={{ fontSize: '0.85rem', fontWeight: 500, color: 'var(--text)' }}>
                {opt.label}
              </div>
              <div style={{ fontSize: '0.75rem', color: 'var(--text3)' }}>
                {opt.description}
              </div>
            </div>
          </label>
        ))}
      </div>
    </div>
  )
}

